import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Row, Col, Badge, Spinner, Button } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL, getBearerToken } from '../../../functions';
import toast from 'react-hot-toast';

const UmrahPackageView = () => {
  const { id } = useParams();
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [airlineData, setAirlineData] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchPackageData = async () => {
      try {
        setLoading(true);
        const tid = toast.loading('Loading package details...');
        
        // Fetch package data
        const packageResponse = await axios.get(`${API_BASE_URL}/umrah-package/${id}`, {
          headers: { Authorization: `Bearer ${getBearerToken()}` }
        });
        
        // Fetch airline data
        const airlineResponse = await axios.get(`${API_BASE_URL}/general`, {
          headers: { Authorization: `Bearer ${getBearerToken()}` }
        });
        
        setPackageData(packageResponse.data.data);
        setAirlineData(airlineResponse?.data?.data?.airline || []);
        toast.dismiss(tid);
      } catch (error) {
        console.error('Error fetching package data:', error);
        toast.error('Failed to load package details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPackageData();
    }
  }, [id]);

  // Helper functions
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getAirlineName = (airlineId) => {
    if (!airlineData || !airlineId) return airlineId;
    const airline = airlineData.find(item => item.id == airlineId);
    return airline ? airline.full_name : airlineId;
  };

  const getAirlineLogo = (airlineId) => {
    if (!airlineData || !airlineId) return null;
    const airline = airlineData.find(item => item.id == airlineId);
    if (airline && airline.logo) {
      const filename = airline.logo.split('/').pop();
      return `${API_BASE_URL}/airline-logo/${filename}`;
    }
    return null;
  };

  const calculateNights = () => {
    if (!packageData?.departure_flights || !packageData?.return_flights || 
        packageData.departure_flights.length === 0 || packageData.return_flights.length === 0) {
      return 0;
    }
    
    const departureDate = packageData.departure_flights[0]?.departure_date;
    const returnDate = packageData.return_flights[0]?.arrival_date;
    
    if (!departureDate || !returnDate) return 0;
    
    const start = new Date(departureDate);
    const end = new Date(returnDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getLowestPrice = () => {
    if (!packageData?.prices || packageData.prices.length === 0) return null;
    const allPrices = [];
    packageData.prices.forEach(p => {
      if (p.price_adult && p.price_adult > 0) allPrices.push(Number(p.price_adult));
      if (p.price_child && p.price_child > 0) allPrices.push(Number(p.price_child));
      if (p.price_infant && p.price_infant > 0) allPrices.push(Number(p.price_infant));
    });
    if (allPrices.length === 0) return null;
    return Math.min(...allPrices);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="text-center py-5">
        <h4>Package not found</h4>
        <p className="text-muted">The requested Umrah package could not be found.</p>
        <Link to="/dashboard/umrah-packages" className="btn btn-primary">
          Back to Packages
        </Link>
      </div>
    );
  }

  const nights = calculateNights();
  const lowestPrice = getLowestPrice();

  return (
    <>
      <div className="main_heading mb-4 d-flex align-items-center justify-content-between">
        <div>
          <h4>Umrah Package Details</h4>
          <p className="text-muted mb-0">View complete package information</p>
        </div>
        <div className="d-flex gap-2">
          <Link to="/dashboard/umrah-packages" className="btn btn-outline-secondary">
            <i className="fas fa-arrow-left me-2"></i>Back to Packages
          </Link>
          <Link to={`/dashboard/edit-umrah/${id}`} className="btn btn-primary">
            <i className="fas fa-edit me-2"></i>Edit Package
          </Link>
        </div>
      </div>

      {/* Package Overview */}
      <Card className="mb-4">
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col lg={8}>
              <div className="mb-3">
                <h2 className="mb-2 text-primary">{packageData.title}</h2>
                {packageData.pnr && (
                  <Badge bg="info" className="mb-2">PNR: {packageData.pnr}</Badge>
                )}
              </div>
              
              <Row className="mb-3">
                <Col md={6}>
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-calendar text-primary me-2"></i>
                    <div>
                      <div className="fontsize12 text-muted">Travel Dates</div>
                      <div className="fontsize15 fontweight700">
                        {packageData.departure_flights?.[0]?.departure_date && packageData.return_flights?.[0]?.arrival_date ? (
                          `${formatDate(packageData.departure_flights[0].departure_date)} - ${formatDate(packageData.return_flights[0].arrival_date)}`
                        ) : (
                          'Dates not available'
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-moon text-warning me-2"></i>
                    <div>
                      <div className="fontsize12 text-muted">Duration</div>
                      <div className="fontsize14 fontweight600">{nights} Nights</div>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-users text-success me-2"></i>
                    <div>
                      <div className="fontsize12 text-muted">Seats</div>
                      <div className="fontsize14 fontweight600">
                        {packageData.available_seats || 0} / {packageData.total_seats || 0} Available
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-tag text-info me-2"></i>
                    <div>
                      <div className="fontsize12 text-muted">Starting Price</div>
                      <div className="fontsize14 fontweight600 text-success">
                        {lowestPrice ? `PKR ${lowestPrice.toLocaleString()}` : 'Price not set'}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Package Features */}
              <div className="mb-3">
                <h6 className="mb-2">Package Features</h6>
                <div className="d-flex flex-wrap gap-2">
                  {packageData.meals_included && (
                    <Badge bg="success">Meals Included</Badge>
                  )}
                  {packageData.zayarat_included && (
                    <Badge bg="info">Zayarat Included</Badge>
                  )}
                  {packageData.non_refundable && (
                    <Badge bg="warning">Non-Refundable</Badge>
                  )}
                  {packageData.non_changeable && (
                    <Badge bg="secondary">Non-Changeable</Badge>
                  )}
                </div>
              </div>

              {/* Discount Information */}
              {(packageData.discount_adult || packageData.discount_child || packageData.discount_infant) && (
                <div className="mb-3">
                  <h6 className="mb-2">Discounts Available</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {packageData.discount_adult && (
                      <Badge bg="success">
                        Adult: {packageData.discount_adult}{packageData.discount_type === 'percentage' ? '%' : ' PKR'}
                      </Badge>
                    )}
                    {packageData.discount_child && (
                      <Badge bg="success">
                        Child: {packageData.discount_child}{packageData.discount_type === 'percentage' ? '%' : ' PKR'}
                      </Badge>
                    )}
                    {packageData.discount_infant && (
                      <Badge bg="success">
                        Infant: {packageData.discount_infant}{packageData.discount_type === 'percentage' ? '%' : ' PKR'}
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {packageData.notes && (
                <div className="mb-3">
                  <h6 className="mb-2">Notes</h6>
                  <p className="text-muted">{packageData.notes}</p>
                </div>
              )}
            </Col>
            
            <Col lg={4}>
              <div className="text-center">
                <div className="mb-3">
                  <div className="fontsize12 text-muted">Starting from</div>
                  {lowestPrice && (
                    <h4 className="mb-0 text-success fontweight700">
                      PKR {lowestPrice.toLocaleString()}
                    </h4>
                  )}
                  <small className="text-muted">per person</small>
                </div>
                
                <Badge 
                  className={`py-2 px-3 ${packageData.status === 'closed' ? 'errorlighterbg redtext' : 'successcolorbg whitetext'}`}
                >
                  {packageData.status === 'closed' ? 'Closed' : 'Active'}
                </Badge>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Flight Information */}
      <Row>
        <Col lg={6}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-plane-departure text-info me-2"></i>
                Departure Flights
              </h5>
            </Card.Header>
            <Card.Body>
              {packageData.departure_flights && packageData.departure_flights.length > 0 ? (
                packageData.departure_flights
                  .sort((a, b) => a.segment_order - b.segment_order)
                  .map((flight, index) => (
                    <div key={index} className="mb-3 p-3 border rounded">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <strong>{flight.flight_number || 'N/A'}</strong>
                          <span className="text-muted ms-2">
                            {getAirlineName(flight.airline_id)}
                          </span>
                        </div>
                        <div className="text-end">
                          <small className="text-muted">
                            {formatDate(flight.departure_date)}
                          </small>
                        </div>
                      </div>
                      
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fontweight600">{flight.departure_airport}</div>
                          <div className="fontsize12 text-muted">
                            {flight.departure_terminal && `Terminal ${flight.departure_terminal}`}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="fontsize12 text-muted">
                            {formatTime(flight.departure_time)}
                          </div>
                          <div className="fontsize12">→</div>
                          <div className="fontsize12 text-muted">
                            {formatTime(flight.arrival_time)}
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="fontweight600">{flight.arrival_airport}</div>
                          <div className="fontsize12 text-muted">
                            {flight.arrival_terminal && `Terminal ${flight.arrival_terminal}`}
                          </div>
                        </div>
                      </div>
                      
                      {(flight.baggage_allowance || flight.hand_carry || flight.meals_included) && (
                        <div className="mt-2 pt-2 border-top">
                          <div className="d-flex flex-wrap gap-2">
                            {flight.baggage_allowance && (
                              <Badge bg="info" className="fontsize10">
                                <i className="fas fa-suitcase me-1"></i>
                                {flight.baggage_allowance}
                              </Badge>
                            )}
                            {flight.hand_carry && (
                              <Badge bg="secondary" className="fontsize10">
                                <i className="fas fa-briefcase me-1"></i>
                                {flight.hand_carry}
                              </Badge>
                            )}
                            {flight.meals_included && (
                              <Badge bg="success" className="fontsize10">
                                <i className="fas fa-utensils me-1"></i>
                                Meals Included
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
              ) : (
                <p className="text-muted">No departure flights configured</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-plane-arrival text-success me-2"></i>
                Return Flights
              </h5>
            </Card.Header>
            <Card.Body>
              {packageData.return_flights && packageData.return_flights.length > 0 ? (
                packageData.return_flights
                  .sort((a, b) => a.segment_order - b.segment_order)
                  .map((flight, index) => (
                    <div key={index} className="mb-3 p-3 border rounded">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <strong>{flight.flight_number || 'N/A'}</strong>
                          <span className="text-muted ms-2">
                            {getAirlineName(flight.airline_id)}
                          </span>
                        </div>
                        <div className="text-end">
                          <small className="text-muted">
                            {formatDate(flight.departure_date)}
                          </small>
                        </div>
                      </div>
                      
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fontweight600">{flight.departure_airport}</div>
                          <div className="fontsize12 text-muted">
                            {flight.departure_terminal && `Terminal ${flight.departure_terminal}`}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="fontsize12 text-muted">
                            {formatTime(flight.departure_time)}
                          </div>
                          <div className="fontsize12">→</div>
                          <div className="fontsize12 text-muted">
                            {formatTime(flight.arrival_time)}
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="fontweight600">{flight.arrival_airport}</div>
                          <div className="fontsize12 text-muted">
                            {flight.arrival_terminal && `Terminal ${flight.arrival_terminal}`}
                          </div>
                        </div>
                      </div>
                      
                      {(flight.baggage_allowance || flight.hand_carry || flight.meals_included) && (
                        <div className="mt-2 pt-2 border-top">
                          <div className="d-flex flex-wrap gap-2">
                            {flight.baggage_allowance && (
                              <Badge bg="info" className="fontsize10">
                                <i className="fas fa-suitcase me-1"></i>
                                {flight.baggage_allowance}
                              </Badge>
                            )}
                            {flight.hand_carry && (
                              <Badge bg="secondary" className="fontsize10">
                                <i className="fas fa-briefcase me-1"></i>
                                {flight.hand_carry}
                              </Badge>
                            )}
                            {flight.meals_included && (
                              <Badge bg="success" className="fontsize10">
                                <i className="fas fa-utensils me-1"></i>
                                Meals Included
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
              ) : (
                <p className="text-muted">No return flights configured</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Hotel Information */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            <i className="fas fa-hotel text-warning me-2"></i>
            Hotel Information
          </h5>
        </Card.Header>
        <Card.Body>
          {packageData.hotels && packageData.hotels.length > 0 ? (
            <Row>
              {packageData.hotels.map((hotel, index) => (
                <Col md={6} key={index} className="mb-3">
                  <div className="p-3 border rounded">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <h6 className="mb-1">{hotel.hotel_name}</h6>
                        <Badge bg="info" className="text-capitalize">
                          {hotel.location}
                        </Badge>
                        {hotel.category && (
                          <Badge bg="secondary" className="ms-1">
                            {hotel.category}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {hotel.distance && (
                      <div className="mb-2">
                        <small className="text-muted">
                          <i className="fas fa-map-marker-alt me-1"></i>
                          Distance: {hotel.distance}
                        </small>
                      </div>
                    )}
                    
                    {hotel.address && (
                      <div>
                        <small className="text-muted">
                          <i className="fas fa-location-dot me-1"></i>
                          {hotel.address}
                        </small>
                      </div>
                    )}
                  </div>
                </Col>
              ))}
            </Row>
          ) : (
            <p className="text-muted">No hotel information available</p>
          )}
        </Card.Body>
      </Card>

      {/* Pricing Information */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            <i className="fas fa-dollar-sign text-success me-2"></i>
            Pricing Information
          </h5>
        </Card.Header>
        <Card.Body>
          {packageData.prices && packageData.prices.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Room Type</th>
                    <th>Adult Price</th>
                    <th>Child Price</th>
                    <th>Infant Price</th>
                  </tr>
                </thead>
                <tbody>
                  {packageData.prices.map((price, index) => (
                    <tr key={index}>
                      <td>
                        <Badge bg="info" className="text-capitalize">
                          {price.room_type === 'sharing' ? 'Quint/Sharing' : price.room_type}
                        </Badge>
                      </td>
                      <td>
                        {price.price_adult ? (
                          <span className="fontweight600 text-success">
                            PKR {Number(price.price_adult).toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-muted">Not set</span>
                        )}
                      </td>
                      <td>
                        {price.price_child ? (
                          <span className="fontweight600 text-success">
                            PKR {Number(price.price_child).toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-muted">Not set</span>
                        )}
                      </td>
                      <td>
                        {price.price_infant ? (
                          <span className="fontweight600 text-success">
                            PKR {Number(price.price_infant).toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-muted">Not set</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted">No pricing information available</p>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default UmrahPackageView;
